import { authV2Url } from '@/core/app/authUrls'
import { readIdTokenSubject } from '@/core/app/idTokenSubject'
import { logger } from '@/core/app/logger'
import { AuthCheckResponseSchema, TokenRefreshSuccessSchema } from '@/schemas/v2/auth'

/**
 * 認証状態を会員単位で表したもの（#3760）。
 * - `{ kind: 'member', sub }`: ログイン中
 * - `{ kind: 'guest' }`: 未ログイン
 * - `{ kind: 'unknown' }`: 判定できない（通信エラー・404 not_found・5xx・スキーマ不一致・sub が読めない）
 */
export type AuthIdentity = { kind: 'member'; sub: string } | { kind: 'guest' } | { kind: 'unknown' }

const identityFromIdToken = (idToken: string): AuthIdentity => {
  const sub = readIdTokenSubject(idToken)
  return sub ? { kind: 'member', sub } : { kind: 'unknown' }
}

/** ページが今使っている会員を useTokenStore の状態から作る */
export const identityFromTokenStore = (state: { idToken: string | null; loading: boolean }): AuthIdentity => {
  // checkAuth の結果がまだ出ていない（phase1 / phase2）ページは、このあと checkAuth が Cookie を確かめるので比べない
  if (state.loading) return { kind: 'unknown' }
  if (state.idToken === null) return { kind: 'guest' }
  return identityFromIdToken(state.idToken)
}

/**
 * Cookie 上の会員を auth サーバーに問い合わせる（#3760）。
 *
 * - auth/check が 200 認証済み → その IdToken の sub
 * - auth/check が 401 no_session → ゲスト
 * - auth/check が 401 token_expired → token/refresh を 1 回だけ呼ぶ。成功ならその sub、401 ならゲスト
 * - それ以外（404 not_found・5xx・通信エラー・スキーマ不一致） → unknown（呼び出し側は何もしない）
 *
 * auth/check は未認証を 401 + JSON（{ authenticated: false, reason }）で返すため、res.ok だけで判定しない。
 *
 * 取得したトークンは useTokenStore に保存しない。会員が同じならページの状態はそのままでよく、
 * 違えば読み直すので、ここでストアを書き換える必要がない（書き換えると「違う会員のトークン + 前の会員のキャッシュ」の
 * 中途半端な状態を一瞬作ってしまう）。
 */
export const fetchCookieIdentity = async (campaignSlug: string): Promise<AuthIdentity> => {
  try {
    const checkRes = await fetch(authV2Url.check(campaignSlug), { credentials: 'include' })
    // 200（認証済み）と 401（no_session / token_expired）だけ本文を読む。404 not_found（キャンペーンなし）・5xx は判定しない
    if (checkRes.status !== 200 && checkRes.status !== 401) return { kind: 'unknown' }
    const check = AuthCheckResponseSchema.parse(await checkRes.json())
    if (check.authenticated) return identityFromIdToken(check.idToken)
    if (check.reason === 'no_session') return { kind: 'guest' }

    // token_expired: RefreshToken で会員を確かめる。リトライはしない（復元・タブ復帰のたびに確かめるので、失敗しても次の機会がある）
    const refreshRes = await fetch(authV2Url.tokenRefresh(campaignSlug), { method: 'POST', credentials: 'include' })
    // 401（unauthorized / invalid_grant）は RefreshToken も無効 = ゲスト。404 not_found（キャンペーンなし）・5xx は判定しない
    if (refreshRes.status === 401) return { kind: 'guest' }
    if (!refreshRes.ok) return { kind: 'unknown' }
    const refreshed = TokenRefreshSuccessSchema.parse(await refreshRes.json())
    return identityFromIdToken(refreshed.idToken)
  } catch (e) {
    logger.warn({ xstate: 'lifecycle', msg: `fetchCookieIdentity: 会員を判定できない: ${String(e)}` })
    return { kind: 'unknown' }
  }
}

/**
 * メモリの会員と Cookie の会員を比べて、ページを読み直すべきか（#3760）。
 * どちらかが unknown のときは判定できないので読み直さない（通信エラーで読み直しを繰り返さないため）。
 */
export const shouldReloadForMemberChange = (inMemory: AuthIdentity, inCookie: AuthIdentity): boolean => {
  if (inMemory.kind === 'unknown' || inCookie.kind === 'unknown') return false
  // 会員 ⇄ ゲスト
  if (inMemory.kind !== inCookie.kind) return true
  if (inMemory.kind === 'member' && inCookie.kind === 'member') return inMemory.sub !== inCookie.sub
  // ゲスト同士
  return false
}
